import React, { useState, useEffect } from 'react';
import { logger, LogLevel, LogEntry } from '../utils/logger';

interface LogViewerProps {
  isVisible: boolean;
  onClose: () => void;
}

const LogViewer: React.FC<LogViewerProps> = ({ isVisible, onClose }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<LogLevel | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isVisible) {
      refreshLogs();
    }
  }, [isVisible]);

  useEffect(() => {
    filterLogs();
  }, [logs, selectedLevel, searchTerm]);

  const refreshLogs = () => {
    setLogs(logger.getLogs());
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filter by level
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(log => log.level === selectedLevel);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(term) ||
        JSON.stringify(log.data).toLowerCase().includes(term)
      );
    }

    setFilteredLogs(filtered.slice(-100)); // Show only last 100 filtered logs
  };

  const clearLogs = () => {
    logger.clearLogs();
    setLogs([]);
    setFilteredLogs([]);
  };

  const downloadLogs = () => {
    logger.downloadLogs();
  };

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case LogLevel.ERROR: return '#dc3545';
      case LogLevel.WARN: return '#ffc107';
      case LogLevel.INFO: return '#17a2b8';
      case LogLevel.DEBUG: return '#6c757d';
      default: return '#000';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-5/6 h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">Application Logs</h2>
          <div className="flex space-x-2">
            <button
              onClick={refreshLogs}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh
            </button>
            <button
              onClick={clearLogs}
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clear
            </button>
            <button
              onClick={downloadLogs}
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 p-4 border-b bg-gray-50">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Level:
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value as LogLevel | 'all')}
              className="px-3 py-1 border border-gray-300 rounded"
            >
              <option value="all">All Levels</option>
              <option value={LogLevel.ERROR}>Error</option>
              <option value={LogLevel.WARN}>Warning</option>
              <option value={LogLevel.INFO}>Info</option>
              <option value={LogLevel.DEBUG}>Debug</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search:
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search logs..."
              className="w-full px-3 py-1 border border-gray-300 rounded"
            />
          </div>
        </div>

        {/* Log List */}
        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-2">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className="border rounded p-3 bg-gray-50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span
                      className="px-2 py-1 rounded text-xs font-bold text-white"
                      style={{ backgroundColor: getLevelColor(log.level) }}
                    >
                      {log.level.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-600">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    {log.userId && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        User: {log.userId}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="font-medium mb-2">{log.message}</div>
                
                {log.data && (
                  <details className="mb-2">
                    <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                      Show Data
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  </details>
                )}
                
                {log.stack && (
                  <details>
                    <summary className="cursor-pointer text-sm text-red-600 hover:text-red-800">
                      Show Stack Trace
                    </summary>
                    <pre className="mt-2 p-2 bg-red-50 rounded text-xs overflow-auto text-red-700">
                      {log.stack}
                    </pre>
                  </details>
                )}
                
                <div className="text-xs text-gray-500 mt-2">
                  {log.url && <div>URL: {log.url}</div>}
                  <div>Session: {log.sessionId}</div>
                </div>
              </div>
            ))}
            
            {filteredLogs.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                No logs found matching the current filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogViewer;